import React from 'react'
import styled from '@emotion/styled'
import { iconSet } from '../icons/iconSet'
import { MultiButtonProps } from '../buttons/MultiButton'
import * as constants from 'view/styles/constants'
import { ButtonStrip, MinimalIconButton } from '../buttons'
import { DropdownAction, DropdownOptions, DropdownOptionsProps } from './DropdownOptions'

interface ToggleButtonProps extends MultiButtonProps {
  menuOpen: boolean
}

const ToggleButton = styled(MinimalIconButton)<ToggleButtonProps>`
  background-color: ${props => (props.menuOpen ? constants.ACCENT_PRIMARY_1 : props.disabled ? constants.CORE_PRIMARY_3 : 'transparent')};
  color: ${props => (props.menuOpen ? constants.CORE_PRIMARY : props.disabled ? constants.CORE_PRIMARY : constants.CORE_PRIMARY_5)};

  &:hover:not(:disabled) {
    background-color: ${props => (props.menuOpen ? constants.ACCENT_PRIMARY_1 : constants.CORE_PRIMARY)};
    border-color: ${props => (props.menuOpen ? constants.ACCENT_PRIMARY_1 : constants.CORE_PRIMARY_3)};
  }
`

export const HoverAccentType = {
  Primary: constants.ACCENT_GOOD_1,
  Bad: constants.ACCENT_BAD_1,
  Secondary: constants.ACCENT_SECONDARY_1
} as const

type HoverAccentType = typeof HoverAccentType[keyof typeof HoverAccentType]

const HoverAccentIconButton = styled(MinimalIconButton)<{
  accentColor: HoverAccentType
  colorIconOnlyOnHover?: boolean
  isVisible?: boolean
}>`
  color: ${props =>
    !props.disabled ? (!props?.colorIconOnlyOnHover ? props.accentColor : constants.CORE_PRIMARY_5) : constants.CORE_PRIMARY_3};
  background-color: transparent;
  visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};

  &:hover:not(:disabled) {
    background-color: ${constants.CORE_PRIMARY};
    border-color: ${props => props.accentColor};
    color: ${props => props.accentColor};
  }
`

interface PositiveNegativeIconButtonDropdownProps<TAction extends DropdownAction> extends Omit<DropdownOptionsProps<TAction>, 'children'> {
  'aria-label'?: string
  showMainActions?: boolean
}

export interface IconActionItem extends DropdownAction {
  hidden?: boolean
  icon?: React.JSX.Element
  accentColor?: HoverAccentType
  type?: PositiveNegativeType
}

export enum PositiveNegativeType {
  Good = 'Good',
  Bad = 'Bad'
}

export const PositiveNegativeIconsButtonDropdown = ({
  'aria-label': ariaLabel,
  'data-test-id': dataTestId,
  showMainActions = true,
  ...props
}: PositiveNegativeIconButtonDropdownProps<IconActionItem>) => {
  const mainPositiveAction =
    props.actions.find(a => a.icon && a.type === PositiveNegativeType.Good && a.enabled) ??
    props.actions.find(a => a.icon && a.type === PositiveNegativeType.Good)
  const mainNegativeAction =
    props.actions.find(a => a.icon && a.type === PositiveNegativeType.Bad && a.enabled) ??
    props.actions.find(a => a.icon && a.type === PositiveNegativeType.Bad)

  if (!mainPositiveAction || !mainNegativeAction) {
    throw new Error('A positive and a negative action are required.')
  }

  return (
    <DropdownOptions data-test-id={dataTestId} {...props}>
      {({ toggle, isOpen }) => (
        <ButtonStrip>
          <HoverAccentIconButton
            aria-label={mainPositiveAction.label}
            accentColor={mainPositiveAction.accentColor ?? HoverAccentType.Primary}
            dataTestId={`SplitIconButtonDropdown-${dataTestId}-Button`}
            disabled={!showMainActions || props.disabled || mainPositiveAction.enabled === false}
            isVisible={showMainActions}
            onClick={evt => handleButtonClick(mainPositiveAction, evt)}>
            {mainPositiveAction.icon}
          </HoverAccentIconButton>
          <HoverAccentIconButton
            aria-label={mainNegativeAction.label}
            accentColor={mainNegativeAction.accentColor ?? HoverAccentType.Bad}
            colorIconOnlyOnHover={true}
            dataTestId={`SplitIconButtonDropdown-${dataTestId}-Button`}
            disabled={props.disabled || mainNegativeAction.enabled === false}
            isVisible={showMainActions}
            onClick={evt => handleButtonClick(mainNegativeAction, evt)}>
            {mainNegativeAction.icon}
          </HoverAccentIconButton>
          <ToggleButton
            aria-label={ariaLabel ?? 'More Options'}
            dataTestId={`SplitButtonDropdown-${dataTestId}-Icon`}
            menuOpen={isOpen}
            onClick={toggle}
            disabled={props.disabled}>
            {iconSet.menuIcon}
          </ToggleButton>
        </ButtonStrip>
      )}
    </DropdownOptions>
  )
}

function handleButtonClick<T extends DropdownAction>(action: T, evt: React.MouseEvent) {
  action.onClick()
  evt.preventDefault()
  evt.stopPropagation()
}
